import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, Loader2, CheckCircle2, Clock } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CallbackButton = ({ variant = "primary", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Vul uw naam en telefoonnummer in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/callbacks`, form);
      setDone(true);
      toast.success("We bellen u zo snel mogelijk terug — binnen 15 minuten.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Er ging iets mis. Probeer het opnieuw of bel ons direct."
      );
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ name: "", phone: "", note: "" });
    setDone(false);
  };

  const baseBtn =
    "group inline-flex items-center justify-center gap-2 font-semibold transition-all hover:-translate-y-0.5";
  const variants = {
    primary:
      "rounded-full bg-[#D97706] hover:bg-[#B45309] text-white px-6 py-4 shadow-lg shadow-amber-200",
    pill:
      "rounded-full bg-white border border-amber-300 text-amber-700 hover:border-amber-500 px-5 py-2.5 text-sm",
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="callback-trigger-btn"
          className={`${baseBtn} ${variants[variant]} ${className}`}
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-100" />
          </span>
          <PhoneCall size={16} />
          Bel mij terug binnen 15 min
        </button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md rounded-2xl"
        data-testid="callback-dialog"
      >
        {done ? (
          <div className="text-center py-6" data-testid="callback-success">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#F0FDF4]">
              <CheckCircle2 size={28} className="text-[#16A34A]" />
            </div>
            <h3 className="mt-5 font-display text-2xl font-bold text-[#0F172A]">
              Bedankt! We bellen u zo terug.
            </h3>
            <p className="mt-2 text-slate-600 text-sm max-w-xs mx-auto">
              U ontvangt binnen 15 minuten een telefoontje van ons team. Houd
              uw toestel bij de hand.
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="callback-close-btn"
              className="mt-7 inline-flex items-center justify-center rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold hover:border-[#0284C7] hover:text-[#0284C7]"
            >
              Sluiten
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left">
              <div className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-700">
                <Clock size={12} /> Reactie binnen 15 minuten
              </div>
              <DialogTitle className="font-display text-2xl font-bold pt-3">
                We bellen u graag terug
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                Laat uw naam en nummer achter — een medewerker neemt zo snel
                mogelijk contact op.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="callback-form">
              <div>
                <Label htmlFor="cb-name">Naam *</Label>
                <Input
                  id="cb-name"
                  data-testid="callback-input-name"
                  placeholder="Uw naam"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mt-1.5 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="cb-phone">Telefoonnummer *</Label>
                <Input
                  id="cb-phone"
                  data-testid="callback-input-phone"
                  placeholder="0466 12 34 56"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-1.5 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="cb-note">Korte notitie (optioneel)</Label>
                <Textarea
                  id="cb-note"
                  data-testid="callback-input-note"
                  placeholder="Bv. type dienst, beste belmoment..."
                  rows={3}
                  value={form.note}
                  onChange={(e) => update("note", e.target.value)}
                  className="mt-1.5 resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="callback-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D97706] hover:bg-[#B45309] disabled:opacity-60 text-white font-semibold px-6 py-3.5 shadow-md shadow-amber-200 transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Versturen...
                  </>
                ) : (
                  <>
                    <PhoneCall size={16} /> Bel mij terug
                  </>
                )}
              </button>
              <p className="text-xs text-slate-500 text-center">
                We gebruiken uw nummer enkel om u terug te bellen.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
